import { gql, useMutation, useQuery } from '@apollo/client';
import * as React from 'react';

import logger from '../../lib/logger';
import validateEmail from '../../lib/validation/emailValidation';
import { formatTimeAgo } from '../../utils/formatting';
import Button from '../base/buttons/Button';
import Modal from '../base/Modal/Modal';

import type {
  AdminGetRecentAlphaAllowListEntriesQuery,
  AdminSearchAlphaAllowListQuery,
  AdminSearchAlphaAllowListQueryVariables,
  AdminAddToAlphaAllowListMutation,
  AdminAddToAlphaAllowListMutationVariables,
  AdminRemoveFromAlphaAllowListMutation,
  AdminRemoveFromAlphaAllowListMutationVariables,
} from './__generated__/AdminAlphaAllowListPane.gql';

const ADMIN_GET_RECENT_ENTRIES = gql`
  query AdminGetRecentAlphaAllowListEntries {
    adminGetRecentAlphaAllowListEntries {
      id
      normalizedEmail
      addedByUserEmail
      createdAt
    }
  }
`;

const ADMIN_SEARCH_ENTRIES = gql`
  query AdminSearchAlphaAllowList($query: String!) {
    adminSearchAlphaAllowList(query: $query) {
      id
      normalizedEmail
      addedByUserEmail
      createdAt
    }
  }
`;

const ADMIN_ADD_TO_ALLOW_LIST = gql`
  mutation AdminAddToAlphaAllowList($email: String!) {
    adminAddToAlphaAllowList(email: $email) {
      id
      normalizedEmail
      addedByUserEmail
      createdAt
    }
  }
`;

const ADMIN_REMOVE_FROM_ALLOW_LIST = gql`
  mutation AdminRemoveFromAlphaAllowList($normalizedEmail: String!) {
    adminRemoveFromAlphaAllowList(normalizedEmail: $normalizedEmail)
  }
`;

const AdminAlphaAllowListPane = () => {
  const [addEmail, setAddEmail] = React.useState('');
  const [addError, setAddError] = React.useState<string | undefined>();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [removeModalOpen, setRemoveModalOpen] = React.useState(false);
  const [emailToRemove, setEmailToRemove] = React.useState<string | null>(null);

  const {
    data: recentData,
    loading: recentLoading,
    refetch: refetchRecent,
  } = useQuery<AdminGetRecentAlphaAllowListEntriesQuery>(ADMIN_GET_RECENT_ENTRIES);

  const {
    data: searchData,
    loading: searchLoading,
    refetch: refetchSearch,
  } = useQuery<AdminSearchAlphaAllowListQuery, AdminSearchAlphaAllowListQueryVariables>(ADMIN_SEARCH_ENTRIES, {
    variables: { query: searchQuery },
    skip: searchQuery.length === 0,
  });

  const [addToAllowList, { loading: addLoading }] = useMutation<
    AdminAddToAlphaAllowListMutation,
    AdminAddToAlphaAllowListMutationVariables
  >(ADMIN_ADD_TO_ALLOW_LIST);

  const [removeFromAllowList] = useMutation<
    AdminRemoveFromAlphaAllowListMutation,
    AdminRemoveFromAlphaAllowListMutationVariables
  >(ADMIN_REMOVE_FROM_ALLOW_LIST);

  const BYPASS_DOMAINS = ['gomantle.com', 'onrepeatnow.com'];

  const isAddValid = React.useMemo(() => {
    const validation = validateEmail(addEmail);
    if (!validation.isValid) {
      return false;
    }
    const domain = addEmail.trim().split('@')[1]?.toLowerCase();
    return !BYPASS_DOMAINS.includes(domain ?? '');
  }, [addEmail]);

  const handleAdd = async () => {
    setAddError(undefined);
    const validation = validateEmail(addEmail);
    if (!validation.isValid) {
      setAddError(validation.error);
      return;
    }
    const domain = addEmail.trim().split('@')[1]?.toLowerCase();
    if (BYPASS_DOMAINS.includes(domain ?? '')) {
      setAddError(`@${domain} emails are already allowed.`);
      return;
    }
    try {
      const result = await addToAllowList({ variables: { email: addEmail.trim() } });
      if (!result.data?.adminAddToAlphaAllowList) {
        alert('Email is already on the alpha allow list.');
      }
      setAddEmail('');
      await refetchRecent();
      if (searchQuery) {
        await refetchSearch();
      }
    } catch (error) {
      logger.error('Failed to add to alpha allow list:', error);
      alert('Failed to add email. Please try again.');
    }
  };

  const handleRemoveConfirm = async () => {
    if (!emailToRemove) {
      return;
    }
    try {
      await removeFromAllowList({ variables: { normalizedEmail: emailToRemove } });
      setRemoveModalOpen(false);
      setEmailToRemove(null);
      await refetchRecent();
      if (searchQuery) {
        await refetchSearch();
      }
    } catch (error) {
      logger.error('Failed to remove from alpha allow list:', error);
      alert('Failed to remove email. Please try again.');
    }
  };

  const entries = searchQuery
    ? (searchData?.adminSearchAlphaAllowList ?? [])
    : (recentData?.adminGetRecentAlphaAllowListEntries ?? []);
  const isLoading = searchQuery ? searchLoading : recentLoading;

  return (
    <div className="flex flex-col gap-6 p-6">
      <h1 className="text-primary text-xl font-semibold">Alpha allow list</h1>

      {/* Add email */}
      <div className="flex items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="add-email" className="text-primary text-sm font-medium">
            Add email to alpha allow list
          </label>
          <input
            id="add-email"
            type="email"
            value={addEmail}
            onChange={(e) => {
              setAddEmail(e.target.value);
              setAddError(undefined);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && isAddValid) {
                void handleAdd();
              }
            }}
            placeholder="user@example.com"
            className="border-primary bg-primary text-primary w-80 rounded-lg border px-3 py-2 text-sm"
          />
          {addError && <span className="text-error-primary text-xs">{addError}</span>}
        </div>
        <Button
          color="primary"
          size="md"
          onClick={() => void handleAdd()}
          isLoading={addLoading}
          isDisabled={!isAddValid}
        >
          Add
        </Button>
      </div>

      {/* Search */}
      <div className="flex flex-col gap-1">
        <label htmlFor="search-email" className="text-primary text-sm font-medium">
          Search by email
        </label>
        <input
          id="search-email"
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search..."
          className="border-primary bg-primary text-primary w-80 rounded-lg border px-3 py-2 text-sm"
        />
      </div>

      {/* Entries table */}
      <div className="flex flex-col gap-2">
        <h2 className="text-primary text-sm font-medium">
          {searchQuery
            ? `Search results (${entries.length})`
            : `${entries.length} recent ${entries.length === 1 ? 'addition' : 'additions'} to allow list`}
        </h2>

        {isLoading ? (
          <span className="text-secondary text-sm">Loading...</span>
        ) : entries.length === 0 ? (
          <span className="text-secondary text-sm">{searchQuery ? 'No results found.' : 'No entries yet.'}</span>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-secondary border-b">
                <th className="pb-2 pr-6 font-medium">Email</th>
                <th className="pb-2 pr-6 font-medium">Added</th>
                <th className="pb-2 pr-6 font-medium">Added by</th>
                <th className="pb-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-b">
                  <td className="text-primary py-2 pr-6">{entry.normalizedEmail}</td>
                  <td className="text-secondary py-2 pr-6">{formatTimeAgo(entry.createdAt)}</td>
                  <td className="text-secondary py-2 pr-6">{entry.addedByUserEmail ?? '—'}</td>
                  <td className="py-2">
                    <Button
                      color="primary-destructive"
                      size="xs"
                      onClick={() => {
                        setEmailToRemove(entry.normalizedEmail);
                        setRemoveModalOpen(true);
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Remove confirmation modal */}
      <Modal isOpen={removeModalOpen} onOpenChange={setRemoveModalOpen}>
        <Modal.Header>Remove from alpha allow list</Modal.Header>
        <Modal.Body>
          <p className="text-primary text-sm">
            Are you sure you want to remove <strong>{emailToRemove}</strong> from the alpha allow list? They will not be
            able to log in until re-added.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button color="secondary" size="sm" onClick={() => setRemoveModalOpen(false)}>
              Cancel
            </Button>
            <Button color="primary-destructive" size="sm" onClick={() => void handleRemoveConfirm()}>
              Remove
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminAlphaAllowListPane;
